import _ from "lodash";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { InputText, Text } from "@pentabd/ui";

import {
  InputDateControllerRow,
  InputTextRow,
  MultiFileRow,
  SelectControllerRow,
} from "@components";

import { useBaseData } from "@hooks/useBaseData";
import { useGeoData } from "@hooks/useGeoData";
import { useLanguage } from "@hooks/useLanguage";

import {
  getConvertedData,
  getConvertedOfficeAddress,
  getConvertedUnitData,
} from "@helpers/baseDataConvert";
import { getConvertedGeoData } from "@helpers/geoDataConvert";

import { duration } from "@utils/duration";
import {
  POSTING_RECORDS,
  PostingRecordPostingFormDataType,
} from "@validations/HRM/employee/postingsValidation";

import { BASE_DATA } from "@constants/base-data";
import { GEO_DATA } from "@constants/geo-data";

const { WINGS, OFFICE } = BASE_DATA;
const { DISTRICTS } = GEO_DATA;

export const PostingRecordPostingForm = ({
  grades,
  isProfileDisabled,
}: {
  grades: any;
  isProfileDisabled: boolean;
}) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  //This data will use for select options
  const { data: districts } = useGeoData(DISTRICTS);
  const { data: office, handleQuery } = useBaseData(OFFICE);
  const { data: wings } = useBaseData(WINGS);

  useEffect(() => {
    handleQuery("search", "");
  }, []);

  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<PostingRecordPostingFormDataType>();

  //This dates will calculate duration
  let fromDate = watch(POSTING_RECORDS.FROM_DATE) as string;
  let toDate = watch(POSTING_RECORDS.TO_DATE) as string;
  const grade = watch(POSTING_RECORDS.GRADE) as string;

  useEffect(() => {
    setValue(
      POSTING_RECORDS.PAY_SCALE_MIN,
      {
        ..._.find(grades, (_grade) => {
          return _grade.id === grade;
        }),
      }.salaryScaleFrom,
    );
    setValue(
      POSTING_RECORDS.PAY_SCALE_MAX,
      {
        ..._.find(grades, (_grade) => {
          return _grade.id === grade;
        }),
      }.salaryScaleTo,
    );
  }, [grade, grades, setValue]);

  return (
    <div className="d-flex flex-column gap-8">
      <InputTextRow
        register={{ ...register(POSTING_RECORDS.POST) }}
        label="EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.POST"
        error={errors}
        required
        disabled={isProfileDisabled}
      />
      <SelectControllerRow
        label="EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.NAME_OF_THE_OFFICE"
        error={errors}
        name={POSTING_RECORDS.OFFICE_ID}
        register={{ ...register(POSTING_RECORDS.OFFICE_ID) }}
        options={getConvertedOfficeAddress(office, language)}
        setValue={setValue}
        control={control}
        isSearchable
        disabled={isProfileDisabled}
      />
      <SelectControllerRow
        label="EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.WING"
        error={errors}
        name={POSTING_RECORDS.UNIT_ID}
        register={{ ...register(POSTING_RECORDS.UNIT_ID) }}
        options={getConvertedUnitData(wings, language)}
        setValue={setValue}
        control={control}
        isSearchable
        required
        disabled={isProfileDisabled}
      />
      <SelectControllerRow
        label="EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.DIST_LOCATION"
        error={errors}
        name={POSTING_RECORDS.DISTRICT_ID}
        register={{ ...register(POSTING_RECORDS.DISTRICT_ID) }}
        options={getConvertedGeoData(districts, language)}
        setValue={setValue}
        control={control}
        isSearchable
        disabled={isProfileDisabled}
      />
      <InputDateControllerRow
        label="EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.FROM_DATE"
        control={control}
        register={{ ...register(POSTING_RECORDS.FROM_DATE) }}
        errors={errors}
        name={POSTING_RECORDS.FROM_DATE}
        required
        disabled={isProfileDisabled}
      />
      <InputDateControllerRow
        label="EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.TO_DATE"
        control={control}
        register={{ ...register(POSTING_RECORDS.TO_DATE) }}
        name={POSTING_RECORDS.TO_DATE}
        disabled={isProfileDisabled}
      />

      {fromDate !== undefined && toDate !== undefined && (
        <div className="row">
          <div className="col-md-12 col-lg-3">
            <Text size="sm" weight="semibold" className="text-title">
              {t(
                "EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.DURATION.LABEL",
              )}
            </Text>
          </div>
          <div className="col-md-12 col-lg-7">
            <Text
              weight="normal"
              size="md"
              component="p"
              color="subtitle2"
              className="px-10 py-5 input-group-wrap input-group-wrap-disabled"
            >
              {duration(fromDate, toDate)
                ? duration(fromDate, toDate)
                : t(
                    "EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.DURATION.PLACEHOLDER",
                  )}
            </Text>
            <Text color="warning" size="xs">
              {t(
                "EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.DURATION.RESTRICTION",
              )}
            </Text>
          </div>
        </div>
      )}

      <SelectControllerRow
        label="EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.GRADE"
        error={errors}
        name={POSTING_RECORDS.GRADE}
        register={{ ...register(POSTING_RECORDS.GRADE) }}
        options={getConvertedData(grades)}
        setValue={setValue}
        control={control}
        required
        disabled={isProfileDisabled}
      />

      <InputTextRow
        register={{ ...register(POSTING_RECORDS.BASIC_SALARY) }}
        label="EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.BASIC_SALARY"
        error={errors}
        disabled={isProfileDisabled}
      />

      <div className="row mb-8">
        <div className="col-md-12 col-lg-3">
          <Text weight="semibold" color="title" size="sm">
            {t(
              "EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.PAY_SCALE.LABEL",
            )}
          </Text>
        </div>

        <div className="d-flex justify-content-center gap-9 align-items-center col-md-12 col-lg-7">
          <div className="flex-fill">
            <InputText
              {...register(POSTING_RECORDS.PAY_SCALE_MIN)}
              disabled
              suffix={
                <Text size="sm" color="subtitle1">
                  {t(
                    "EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.PAY_SCALE.PLACEHOLDER",
                  )}
                </Text>
              }
            />
          </div>
          <Text weight="semibold" color="title" size="sm">
            {t("EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.PAY_SCALE.TO")}
          </Text>
          <div className="flex-fill">
            <InputText
              {...register(POSTING_RECORDS.PAY_SCALE_MAX)}
              disabled
              suffix={
                <Text size="sm" color="subtitle1">
                  {t(
                    "EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.PAY_SCALE.PLACEHOLDER",
                  )}
                </Text>
              }
            />
          </div>
        </div>
      </div>

      <MultiFileRow
        name="document"
        control={control}
        setValue={setValue}
        register={register}
        error={errors}
        fileDetails={["documentName", "date", "remarks", "memorialNo"]}
        disabled={isProfileDisabled}
      />
    </div>
  );
};
